import React, { useCallback, useState } from "react";
import jsonExport from "jsonexport/dist";

import { useMediaQuery, Typography } from "@material-ui/core";

import {
  downloadCSV,
  useListContext,
  BulkExportButton,
  Button,
  DateField,
  DateInput,
  FilterButton,
  FunctionField,
  List,
  RefreshButton,
  SimpleList,
  TextField,
  TopToolbar,
} from "react-admin";

import { ExportIcon, LaunchIcon } from "../../../components/common/iconography";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { getYardCalendarDaysInclusive } from "../../../utils/dateUtils";
import { Pagination } from "../../../components/Pagination";
import { SearchField } from "../../../components/common/SearchField";
import CustomizableDatagrid from "../../../components/common/ra-customizable-datagrid";
import { CarrierType, InboundStatus } from "../../../utils/constants";
import {
  deepRedirect,
  exportCustomField,
  getBackPath,
} from "../../../utils/utils";
import { BackButton } from "../../../components/common/BackButton";
import { AccountFilter, getCustomFieldColumns } from "../ReportsUtils";

// Demurrage runs notified through released, both days counted. Rows saved
// before the clock was stored count the same way here.
const demurrageDays = (inbound) =>
  inbound.daysOnDemurrageClock ||
  getYardCalendarDaysInclusive(
    inbound.notifyDateTime,
    inbound.releaseDateTime,
  ) ||
  0;

const exporter = (inbounds) => {
  const customHeaderMap = new Map();

  const inboundsForExport = inbounds.map((inbound) => {
    const inboundForExport = {};
    inboundForExport.railCarNumber = inbound.railCarNumber;
    inboundForExport.carrierName = inbound.carrierName;
    inboundForExport.notifyDateTime = new Date(
      inbound.notifyDateTime,
    ).toLocaleDateString();
    inboundForExport.placedDateTime = new Date(
      inbound.placedDateTime,
    ).toLocaleDateString();
    inboundForExport.releaseDateTime = new Date(
      inbound.releaseDateTime,
    ).toLocaleDateString();
    inboundForExport.timeOnClock = demurrageDays(inbound);

    // Custom fields carry their own name in the stored value, so the export
    // headers come off the records rather than the account definitions.
    inbound.customFields?.forEach((field) =>
      exportCustomField(field, inboundForExport, customHeaderMap),
    );

    return inboundForExport;
  });

  jsonExport(
    inboundsForExport,
    {
      rename: [
        "Car/Trl Number",
        "Carrier Name",
        "Time Notified",
        "Time Placed",
        "Time Released",
        "Time on Clock (Days)",
      ],
    },
    (err, csv) => {
      downloadCSV(csv, `inboundDemurrage_${new Date().toLocaleDateString()}`); // download as 'inboundDemurrage.csv` file
    },
  );
};

const InboundSearchField = (props) => {
  const { label, alwaysOn } = props;
  const { displayedFilters, filterValues, setFilters } = useListContext(props);

  const handleSearch = (value) => {
    debugger;
    if (value) {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(
        {
          ...nextFilterValues,
          or: [
            { normalizedRailCarNumber: { matchPhrasePrefix: `${value}` } },
            { normalizedRailCarNumber: { wildcard: `*${value}*` } },
            { carrierName: { matchPhrasePrefix: `${value}` } },
            { carrierName: { wildcard: `*${value}*` } },
          ],
        },
        displayedFilters,
      );
    } else {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(nextFilterValues, displayedFilters);
    }
  };

  return (
    <SearchField
      id="searchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
    />
  );
};

const getListFilters = (onAccountChange) => [
  <AccountFilter
    source="accountId.eq"
    onAccountChange={onAccountChange}
    alwaysOn
  />,
  <InboundSearchField label="Search" alwaysOn />,
  <DateInput
    source="notifyDateTime.lte"
    label="Notified Before..."
    variant="outlined"
    resettable
    onKeyPress={(event) => event.preventDefault()}
  />,
  <DateInput
    source="notifyDateTime.gte"
    label="Notified After..."
    variant="outlined"
    resettable
    onKeyPress={(event) => event.preventDefault()}
  />,
  <DateInput
    source="releaseDateTime.lte"
    label="Released Before..."
    variant="outlined"
    resettable
    onKeyPress={(event) => event.preventDefault()}
  />,
  <DateInput
    source="releaseDateTime.gte"
    label="Released After..."
    variant="outlined"
    resettable
    onKeyPress={(event) => event.preventDefault()}
  />,
];

const ListActions = (props) => {
  return (
    <TopToolbar>
      <FilterButton />
      <RefreshButton />
      <BackButton basePath="/reports" />
    </TopToolbar>
  );
};

const BulkListActions = (props) => (
  <BulkExportButton {...props} color="defalut" icon={<ExportIcon />} />
);

export const InboundDemurrageList = (props) => {
  const defaultSortOrder = { field: "releaseDateTime", order: "ASC" };
  const initFilterValues = {
    searchInboundShipments: {
      reloadYardId: { eq: window.appUser?.reloadYardId },
      status: { eq: InboundStatus.RELEASED },
      carrierType: { eq: CarrierType.RAIL },
    },
  };
  // The account itself, not just the filtered id: its custom field
  // definitions name the selectable custom field columns.
  const [account, setAccount] = useState(null);
  const handleAccountChange = useCallback(
    (selectedAccount) => setAccount(selectedAccount || null),
    [],
  );

  return (
    <List
      {...props}
      title={<ForceTitle title="Inbound Demurrage" />}
      filter={initFilterValues}
      filters={getListFilters(handleAccountChange)}
      sort={defaultSortOrder}
      actions={<ListActions />}
      bulkActionButtons={<BulkListActions />}
      exporter={exporter}
      pagination={<Pagination />}
    >
      <ShipmentGrid
        account={account}
        history={props.history}
        backPath={getBackPath(props)}
      />
    </List>
  );
};

const ShipmentGrid = ({ account, history, backPath, ...props }) => {
  const isXSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  // Only offer the custom field columns while an account filter is applied;
  // inbound shipments store values from inboundCustomFields.
  const accountFields =
    account?.inboundCustomFields?.length > 0 ? account.inboundCustomFields : [];

  return isXSmall ? (
    <SimpleList
      {...props}
      primaryText={(record) => record.railCarNumber}
      secondaryText={(record) => (
        <>
          <Typography>{`Notified: ${new Date(
            record.notifyDateTime,
          ).toLocaleDateString()}`}</Typography>
          <Typography color="textSecondary">{`Released: ${new Date(
            record.releaseDateTime,
          ).toLocaleDateString()}`}</Typography>
        </>
      )}
      tertiaryText={(record) => `${demurrageDays(record)} Days`}
      // Same Open button as the desktop grid, for the same reason: the row's
      // own link would navigate without the deepRedirect state, and the
      // shipment's Back button would return to the inbound list, not here.
      rightIcon={(record) => (
        <Button
          label="Open"
          onClick={() =>
            deepRedirect(
              history,
              `/inboundShipments/${record.id}`,
              backPath,
              {},
            )
          }
        >
          <LaunchIcon />
        </Button>
      )}
      linkType={false}
    />
  ) : (
    <CustomizableDatagrid
      rowClick={false}
      {...props}
      storageProp={`InboundDemurrageList_${account?.id || "all"}_large`}
      defaultColumns={[
        "railCarNumber",
        "carrierName",
        "notifyDateTime",
        "placedDateTime",
        "releaseDateTime",
        "daysOnDemurrageClock",
      ]}
    >
      {[
        <TextField
          key="railCarNumber"
          source="railCarNumber"
          label="Car/Trl No"
          sortable={true}
        />,
        <TextField
          key="carrierName"
          source="carrierName"
          label="Carrier Name"
          sortable={true}
        />,
        <DateField
          key="notifyDateTime"
          source="notifyDateTime"
          label="Time Notified"
          showTime={true}
          sortable={true}
        />,
        <DateField
          key="placedDateTime"
          source="placedDateTime"
          label="Time Placed"
          showTime={true}
          sortable={true}
        />,
        <DateField
          key="releaseDateTime"
          source="releaseDateTime"
          label="Time Released"
          showTime={true}
          sortable={true}
        />,
        <FunctionField
          key="daysOnDemurrageClock"
          source="daysOnDemurrageClock"
          label="Time on Clock"
          render={(record) => `${demurrageDays(record)} Days`}
          sortable={true}
        />,
        ...getCustomFieldColumns(accountFields),
        // No source, so the column selector can't hide it: the row itself is
        // no longer clickable, this button is the only way into the shipment.
        // deepRedirect rather than a plain link so the shipment's Back button
        // returns to this report instead of the inbound list.
        <FunctionField
          key="open"
          label=""
          render={(record) => (
            <Button
              label="Open"
              onClick={() =>
                deepRedirect(
                  history,
                  `/inboundShipments/${record.id}`,
                  backPath,
                  {},
                )
              }
            >
              <LaunchIcon />
            </Button>
          )}
        />,
      ]}
    </CustomizableDatagrid>
  );
};
